import React from 'react';
import { stripSystemReminders } from '../../utils/stripSystemReminders';
import { replaceCompactionRequest } from '../../utils/replaceCompactionRequest';
import { stripInterruptMarkers } from '../../utils/stripInterruptMarkers';

interface MarkdownContentProps {
  content: string;
  variant?: 'user' | 'agent' | 'thinking';
  className?: string;
}

// Simple text formatter that handles **bold**, __italic__, and `code` inline
function formatText(text: string, variant: 'user' | 'agent' | 'thinking'): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  let currentIndex = 0;
  let keyCounter = 0;

  // Colors based on variant
  const codeColors = {
    user: 'text-gray-700 bg-gray-100',
    agent: 'text-gray-700 bg-gray-100',
    thinking: 'text-gray-600 bg-gray-50'
  };

  // Pattern to match **bold**, __italic__, or `code`
  const pattern = /(\*\*.*?\*\*|__.*?__|`[^`]+`)/g;
  let match;

  while ((match = pattern.exec(text)) !== null) {
    // Add text before the match
    if (match.index > currentIndex) {
      parts.push(text.slice(currentIndex, match.index));
    }

    const matched = match[0];
    if (matched.startsWith('**') && matched.endsWith('**')) {
      // Bold
      parts.push(
        <strong key={keyCounter++} className="font-bold">
          {matched.slice(2, -2)}
        </strong>
      );
    } else if (matched.startsWith('__') && matched.endsWith('__')) {
      // Italic
      parts.push(
        <em key={keyCounter++} className="italic">
          {matched.slice(2, -2)}
        </em>
      );
    } else if (matched.startsWith('`') && matched.endsWith('`')) {
      // Inline code
      parts.push(
        <code key={keyCounter++} className={`px-1 rounded ${codeColors[variant]}`}>
          {matched.slice(1, -1)}
        </code>
      );
    }

    currentIndex = match.index + matched.length;
  }

  // Add remaining text
  if (currentIndex < text.length) {
    parts.push(text.slice(currentIndex));
  }

  return parts.length > 0 ? parts : [text];
}

const MarkdownContent: React.FC<MarkdownContentProps> = ({
  content,
  variant = 'agent',
  className = '',
}) => {
  let processedContent = stripSystemReminders(content);
  processedContent = replaceCompactionRequest(processedContent);

  if (variant === 'agent') {
    const cleanedContent = stripInterruptMarkers(processedContent);
    if (cleanedContent === null) {
      return null;
    }
    processedContent = cleanedContent;
  }

  // Split into lines and process each
  const lines = processedContent.split('\n');
  const elements: React.ReactNode[] = [];
  let keyCounter = 0;

  // Color schemes for variants
  const textColor = variant === 'thinking' ? 'text-gray-400' : 'text-gray-900';
  const isItalic = variant === 'thinking';

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Check for headers (# ## ### etc)
    const headerMatch = line.match(/^(#{1,6})\s+(.*)$/);
    if (headerMatch) {
      const headerText = headerMatch[2];
      elements.push(
        <div key={keyCounter++} className={`font-bold ${textColor} ${isItalic ? '' : 'not-italic'} my-2`}>
          {formatText(headerText, variant)}
        </div>
      );
      continue;
    }

    // Check for empty lines
    if (line.trim() === '') {
      elements.push(<br key={keyCounter++} />);
      continue;
    }

    // Regular text line
    elements.push(
      <div key={keyCounter++} className={`${textColor} ${isItalic ? 'italic' : ''}`}>
        {formatText(line, variant)}
      </div>
    );
  }

  return (
    <div className={`whitespace-pre-wrap ${className}`}>
      {elements}
    </div>
  );
};

export default MarkdownContent;
